// One appliance alone on a phase — 801038's office AC on phase B. When it
// runs, for how long, what it costs, whether it was left on after hours or on
// the off day, and whether its running power is creeping up.
// Runs: core/devices/energyData.loadRuns, maths: core/devices/applianceRuns.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { loadRuns } from '../../../core/devices/energyData';
import { applianceReport, clock, mergeRuns } from '../../../core/devices/applianceRuns';
import { marginalRate } from '../../../core/devices/energyTariff';
import { Section, Stat } from './EnergySections';
import {
  PHASE, duration, kwh, rupees, rupees2, shortDate, watts,
} from './energyFormat';
import { FIT_XS } from '../fitDialog';

const statGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 1.5 };

export function ApplianceDialog({
  open, value, phases, onClose, onSave, onRemove,
}) {
  const [v, setV] = useState(value);
  useEffect(() => { if (open) setV(value); }, [open, value]);
  if (!v) return null;
  return (
    <Dialog open={open} onClose={onClose} {...FIT_XS}>
      <DialogTitle>{onRemove ? 'Appliance' : 'Track an appliance'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <Typography variant="body2" color="text.secondary">
          For one appliance that is alone on a phase — like an AC on its own line. Everything that phase draws at or above the threshold is counted as this appliance running.
        </Typography>
        <TextField label="Name" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} />
        <TextField select label="Phase" value={v.phase} onChange={(e) => setV({ ...v, phase: Number(e.target.value) })}>
          {phases.map((p) => <MenuItem key={p} value={p}>{PHASE[p].name}</MenuItem>)}
        </TextField>
        <TextField
          type="number"
          label="Running when at least"
          value={v.minW}
          onChange={(e) => setV({ ...v, minW: e.target.value })}
          InputProps={{ endAdornment: 'W' }}
          helperText="Above any small load that shares the phase, below the appliance's lowest running power. 500 W suits a 1–2 ton AC."
        />
      </DialogContent>
      <DialogActions>
        {onRemove && <Button color="error" onClick={onRemove} sx={{ mr: 'auto' }}>Stop tracking</Button>}
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave({ ...v, minW: Number(v.minW) || 500 })} disabled={!String(v.name).trim()}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ApplianceCard({
  uno, appliance, settings, from, to, totalKwh, offline, onEdit,
}) {
  const theme = useTheme();
  const [runs, setRuns] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    setRuns(null);
    setErr('');
    loadRuns(uno, appliance.phase, appliance.minW, from, to)
      .then((r) => { if (alive) setRuns(mergeRuns(r.runs)); })
      .catch(() => { if (alive) { setRuns([]); setErr('Could not load the runs.'); } });
    return () => { alive = false; };
  }, [uno, appliance.phase, appliance.minW, +from, +to]); // eslint-disable-line react-hooks/exhaustive-deps

  const rep = useMemo(() => (runs ? applianceReport(runs, settings, from, to) : null), [runs, settings, from, to]);
  const rate = marginalRate(settings.tariff);

  const header = (
    <>
      <Chip size="small" label={`${PHASE[appliance.phase].name} · ≥ ${appliance.minW} W`} />
      <IconButton size="small" aria-label="Appliance settings" onClick={onEdit}><SettingsIcon fontSize="small" /></IconButton>
    </>
  );

  if (!rep) {
    return (
      <Section title={appliance.name} icon={<AcUnitIcon fontSize="small" sx={{ color: 'text.secondary' }} />} right={header}>
        {err ? <Alert severity="warning">{err}</Alert> : <CircularProgress size={22} />}
      </Section>
    );
  }

  const t = rep.totals;
  const share = totalKwh > 0 ? Math.round((t.kwh / totalKwh) * 100) : null;
  const days = rep.daily.filter((x) => x.hours > 0 || x.type === 'work');
  const chart = rep.daily.map((x) => ({ d: shortDate(x.d), h: +x.hours.toFixed(2), off: x.type === 'off' }));
  const alertKwh = rep.alerts.reduce((a, x) => a + x.kwh, 0);

  return (
    <Section title={appliance.name} icon={<AcUnitIcon fontSize="small" sx={{ color: 'text.secondary' }} />} right={header}>
      <Box sx={statGrid}>
        <Stat label="Units" value={kwh(t.kwh, 0)} sub={`${rupees(t.kwh * rate)}${share != null ? ` · ${share}% of all units` : ''}`} />
        <Stat label="Per work day" value={`${t.avgHoursWorkDay.toFixed(1)} h`} sub={`${kwh(t.avgKwhWorkDay, 1)} · ${rupees(t.avgKwhWorkDay * rate)}`} />
        <Stat label="Usually on → off" value={t.medianOn ? `${t.medianOn} → ${t.medianOff}` : '—'} sub={`used on ${t.daysUsed} days`} />
        <Stat label="Running power" value={watts(t.avgW)} sub={`max ${watts(t.maxW)}${t.pf != null ? ` · PF ${t.pf.toFixed(2)}` : ''}`} />
        {rep.trendPct != null && (
          <Stat
            label="Power trend"
            value={`${rep.trendPct > 0 ? '+' : ''}${rep.trendPct}%`}
            color={rep.trendPct >= 10 ? 'warning.main' : undefined}
            sub={`${watts(rep.weekly[0].avgW)} → ${watts(rep.weekly[rep.weekly.length - 1].avgW)}, first week → last`}
          />
        )}
      </Box>

      {rep.trendPct != null && rep.trendPct >= 10 && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'warning.main' }}>
          It draws {rep.trendPct}% more power while running than it did at the start of this window. Hotter weather explains some of that; if it keeps climbing, have the filter and coils cleaned and the gas checked.
        </Typography>
      )}

      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>Hours running per day</Typography>
      <ResponsiveContainer width="100%" height={150}>
        <BarChart data={chart} margin={{ top: 4, right: 6, bottom: 0, left: -18 }}>
          <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="d" tick={{ fill: theme.palette.text.secondary, fontSize: 10 }} stroke={theme.palette.divider} minTickGap={8} />
          <YAxis tick={{ fill: theme.palette.text.secondary, fontSize: 10 }} stroke={theme.palette.divider} width={36} />
          <Tooltip
            contentStyle={{ background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: 8, fontSize: 12 }}
            formatter={(v) => [`${v} h`, 'Running']}
          />
          <Bar dataKey="h" fill={PHASE[appliance.phase].color} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>

      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {rep.alerts.length ? `Left on outside work hours — ${rep.alerts.length} time${rep.alerts.length > 1 ? 's' : ''}, ${kwh(alertKwh, 1)}, ${rupees(alertKwh * rate)}` : 'Outside work hours'}
      </Typography>
      {rep.alerts.length === 0 ? (
        <Typography variant="body2" color="text.secondary">Never ran outside work hours or on the off day in this window.</Typography>
      ) : rep.alerts.slice(-6).reverse().map((a) => (
        <Box key={`${a.d}${clock(a.s)}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.25 }}>
          <Typography variant="body2">
            {`${shortDate(a.d)} ${clock(a.s)}–${clock(a.e)}${a.offDay ? ' (off day)' : ''}`}
          </Typography>
          <Typography variant="body2" color="warning.main">{`${duration(a.offSeconds)} after hours · ${rupees(a.kwh * rate)}`}</Typography>
        </Box>
      ))}

      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Day by day</Typography>
      <Box sx={{ overflowX: 'auto', maxHeight: 280, overflowY: 'auto' }}>
        <Table size="small" stickyHeader sx={{ '& td, & th': { px: 0.75, whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow>
              <TableCell>Day</TableCell>
              <TableCell>On → off</TableCell>
              <TableCell align="right">Hours</TableCell>
              <TableCell align="right">Units</TableCell>
              <TableCell align="right">₹</TableCell>
              <TableCell align="right">Starts</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...days].reverse().map((x) => (
              <TableRow key={x.d}>
                <TableCell>{`${shortDate(x.d)}${x.type === 'off' ? ' · off' : ''}`}</TableCell>
                <TableCell sx={{ color: x.hours > 0 ? undefined : 'text.secondary' }}>
                  {x.hours > 0 ? `${clock(x.first)} → ${clock(x.last)}` : (offline && offline.has(x.d) ? 'sensor offline' : 'not used')}
                </TableCell>
                <TableCell align="right">{x.hours > 0 ? x.hours.toFixed(1) : '—'}</TableCell>
                <TableCell align="right">{x.hours > 0 ? x.kwh.toFixed(1) : '—'}</TableCell>
                <TableCell align="right">{x.hours > 0 ? rupees(x.kwh * rate) : '—'}</TableCell>
                <TableCell align="right">{x.starts || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        ₹ at {rupees2(rate)} per unit — the formula's per-unit charges with duty. "Sensor offline" = no readings for most of that day, so the appliance may have run unseen.
      </Typography>
    </Section>
  );
}
